import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import StatusBadge from "../../components/StatusBadge";
import { ChevronRight, MapPin, CalendarDays, History, UserCircle } from "lucide-react";

const fmtPrice = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n || 0);
const fmtDate = (s) => {
  if (!s) return "-";
  try { return new Date(s).toLocaleDateString("fr-FR"); } catch { return "-"; }
};

export default function CompanyOrders() {
  const [orders, setOrders] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/orders"), api.get("/client/vendors")])
      .then(([o, v]) => { setOrders(o.data); setVendors(v.data); })
      .finally(() => setLoading(false));
  }, []);

  const shown = filter ? orders.filter((o) => o.vendor_id === filter) : orders;

  return (
    <div data-testid="company-orders-page">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div>
          <div className="lc-small-label mb-1">Espace administrateur</div>
          <h1 className="display text-3xl sm:text-4xl font-extrabold tracking-tight flex items-center gap-3">
            <History className="w-7 h-7 text-[var(--lc-orange)]" /> Toutes les commandes
          </h1>
        </div>
        <select
          data-testid="company-vendor-filter"
          className="lc-input w-auto text-sm"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        >
          <option value="">Tous les vendeurs</option>
          {vendors.map((v) => (
            <option key={v.id} value={v.id}>{v.first_name} {v.last_name}</option>
          ))}
        </select>
      </div>

      {loading && <div className="text-sm text-[var(--lc-gray)]">Chargement…</div>}
      {!loading && shown.length === 0 && (
        <div className="lc-card p-10 text-center" data-testid="company-no-orders">
          <div className="text-base font-semibold mb-1">Aucune commande</div>
          <p className="text-sm text-[var(--lc-gray)]">Les demandes créées par vos vendeurs apparaîtront ici.</p>
        </div>
      )}

      <div className="grid gap-3" data-testid="company-orders-list">
        {shown.map((o) => (
          <Link
            to={`/company/orders/${o.id}`}
            key={o.id}
            data-testid={`company-order-row-${o.id}`}
            className="lc-card p-4 sm:p-5 grid sm:grid-cols-[1fr,auto] gap-4 hover:border-[var(--lc-orange)]/40 transition-colors"
          >
            <div>
              <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                <span className="font-mono text-xs text-[var(--lc-gray)]">{o.ref}</span>
                <StatusBadge status={o.status} />
                {o.vendor_name && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-black/5 rounded px-1.5 py-0.5 text-[var(--lc-dark-gray)]">
                    <UserCircle className="w-3 h-3" /> {o.vendor_name}
                  </span>
                )}
              </div>
              <div className="font-semibold text-sm flex items-start gap-1.5">
                <MapPin className="w-3.5 h-3.5 mt-0.5 text-[var(--lc-orange)] shrink-0" />
                {o.arrival_address}
              </div>
              <div className="text-xs text-[var(--lc-gray)] mt-1 flex items-center gap-3">
                <span className="inline-flex items-center gap-1"><CalendarDays className="w-3 h-3" /> {fmtDate(o.delivery_date_confirmed || o.delivery_date_wanted)}</span>
                <span>{o.distance_km} km</span>
                <span>{o.package_name || "—"}</span>
              </div>
            </div>
            <div className="flex sm:flex-col sm:items-end justify-between gap-2">
              <div className="text-right">
                <div className="text-xl font-black text-[var(--lc-orange)]">{o.on_quote ? "Sur devis" : fmtPrice(o.total_ttc)}</div>
                {!o.on_quote && <div className="text-[10px] text-[var(--lc-gray)]">{fmtPrice(o.total_ht)} HT</div>}
              </div>
              <ChevronRight className="w-4 h-4 text-[var(--lc-gray)]" />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
